import React, { useState } from 'react'
import styled from 'styled-components'

const DropdownContainer = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin: 0 auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
`

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 16px;
`

const CodeBlock = styled.pre`
  background-color: #272822;
  color: #f8f8f2;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`

interface LanguageDropdownProps {
  url: string
  headers?: { [key: string]: string }
  disableBackground?: boolean
  title?: string
}

const CodeSnippet: React.FC<LanguageDropdownProps> = ({ url, headers = {}, disableBackground = false, title = '' }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('curl')

  const parsedUrl = new URL(url)
  const host = parsedUrl.host

  const headersString = Object.entries(headers)
    .map(([key, value]) => `-H "${key}: ${value}"`)
    .join(' ')

  const headersHttpString = Object.entries(headers)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n')

  const headersJavaScriptString = Object.entries(headers)
    .map(([key, value]) => `'${key}': '${value}'`)
    .join(',\n    ')

  const headersPythonString = Object.entries(headers)
    .map(([key, value]) => `'${key}': '${value}'`)
    .join(',\n    ')

  const codeSnippets: { [key: string]: string } = {
    curl: `curl -X GET "${url}" ${headersString}`,
    http: `GET ${parsedUrl.pathname}?${parsedUrl.searchParams} HTTP/1.1
Host: ${host}
${headersHttpString}`,
    java: `import java.io.IOException;
import java.net.HttpURLConnection;
import java.net.URL;
import java.util.Scanner;

public class Main {
    public static void main(String[] args) throws IOException {
        URL url = new URL("${url}");
        HttpURLConnection conn = (HttpURLConnection) url.openConnection();
        conn.setRequestMethod("GET");
        ${Object.entries(headers).map(([key, value]) => `conn.setRequestProperty("${key}", "${value}");`).join('\n        ')}
        conn.connect();

        int responseCode = conn.getResponseCode();
        if (responseCode != 200) {
            throw new RuntimeException("HttpResponseCode: " + responseCode);
        } else {
            Scanner sc = new Scanner(url.openStream());
            while (sc.hasNext()) {
                System.out.println(sc.nextLine());
            }
            sc.close();
        }
    }
}`,
    javascript: `fetch('${url}', {
  headers: {
    ${headersJavaScriptString}
  }
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`,
    python: `import requests

headers = {
    ${headersPythonString}
}

response = requests.get('${url}', headers=headers)
if response.status_code == 200:
    print(response.json())
else:
    print('Error:', response.status_code)`
  }

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(event.target.value)
  }

  const content = (
    <>
      {title && <Label htmlFor='language-select'>{title}</Label>}
      <Select id='language-select' value={selectedLanguage} onChange={handleLanguageChange}>
        <option value='curl'>cURL</option>
        <option value='http'>HTTP</option>
        <option value='java'>Java</option>
        <option value='javascript'>JavaScript</option>
        <option value='python'>Python</option>
      </Select>
      {selectedLanguage && (
        <CodeBlock>
          <code>{codeSnippets[selectedLanguage]}</code>
        </CodeBlock>
      )}
    </>
  )

  return disableBackground ? content : <DropdownContainer>{content}</DropdownContainer>
}

export default CodeSnippet
